import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Target } from '../../../../client';
import SimpleInfosModal from '../../../../components/SimpleInfosModal';
import { getPortalRootDOMElement } from '../../../../DOMElements';
import useMergeProps from '../../../../hooks/useMergeProps';
import useRouter from '../../../../hooks/useRouter';
import { __ } from '../../../../i18n/l10n';
import { selectUserConnected } from '../../../../selectors/user/userPrimary.selector';
import { StateType } from '../../../../states';
import { PromotionStatus, PromotionStatuses } from '../../../../types/promotions';
import MissionsActionCreators from '../../actions/missions';
import InnerOptInErrorContent from '../common/InnerOptInErrorContent/InnerOptInErrorContent';

import MissionEnrollmentButton from './MissionEnrollmentButton';
import selectMissionEnrollmentButtonContainerProps from './MissionEnrollmentButton.selector';

const optInErrorCodesLabels: Record<string, string> = {
  // __('You are currently not eligible to betting promotions')
  '-30401': 'You are currently not eligible to betting promotions',
  // __('Internal error')
  '-30404': 'Internal error',
  // __('!desc:userAlreadyOptedIn')
  '-30409': '!desc:userAlreadyOptedIn',
  // __('Internal error')
  '-30500': 'Internal error',
  // __('You are currently not eligible to betting promotions')
  undefined: 'You are currently not eligible to betting promotions',
};

const optInErrorCodes = {
  USER_NOT_ELIGIBLE_BETTING_PROMOTION: '-30401',
  USER_NOT_REGISTERED_PROMOTIONS: '50401',
};

const StyledSimpleInfosModal = styled(SimpleInfosModal)`
  padding: 1em 2em;
  background: #030303;
`;

type MissionEnrollmentButtonContainerProps = {
  className?: string;
  missionId: number;
  isWon?: boolean;
};

type OptInErrorType = {
  code: string;
  data: {
    reason: string;
    promotionStatus: PromotionStatus;
  };
};

const MissionEnrollmentButtonContainer = ({ missionId, ...ownProps }: MissionEnrollmentButtonContainerProps) => {
  const dispatch = useDispatch();
  const { history, location } = useRouter();

  const [optInError, setOptInError] = useState<OptInErrorType | null>(null);
  const errorCodeLabel = useMemo(() => {
    const { code, data } = optInError ?? {};
    const { reason, promotionStatus } = data ?? {};
    const existingPromotionStatuses: Array<PromotionStatus> = Object.values(PromotionStatuses);

    if (reason === 'UserNotAllowed' && promotionStatus && !!~existingPromotionStatuses.indexOf(promotionStatus)) {
      return <InnerOptInErrorContent missionId={missionId} promotionStatus={promotionStatus} />;
    }

    if (
      [optInErrorCodes.USER_NOT_REGISTERED_PROMOTIONS, optInErrorCodes.USER_NOT_ELIGIBLE_BETTING_PROMOTION].includes(
        code ?? '',
      )
    ) {
      return <InnerOptInErrorContent missionId={missionId} missionsModerator />;
    }

    return __(optInErrorCodesLabels[`${code}`]);
  }, [missionId, optInError]);
  const [optInErrorModalIsOpened, setOptInErrorModalIsOpened] = useState(!!optInError);

  useEffect(() => {
    if (optInError) {
      setOptInErrorModalIsOpened(true);
    }
  }, [optInError]);

  const isUserConnected = useSelector(selectUserConnected);

  const selectorProps = useMemo(() => ({ missionId }), [missionId]);
  const selectedProps = useSelector((state: StateType) =>
    selectMissionEnrollmentButtonContainerProps(state, selectorProps),
  );

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();

      if (isUserConnected) {
        dispatch(
          MissionsActionCreators.requestOptInUser(
            { missionId },
            {
              onError: (error: OptInErrorType) => {
                setOptInError(error);
              },
            },
          ),
        );
      } else {
        if (Target.__MOBILE__) {
          history.push({
            pathname: '/',
            hash: '#login',
            state: {
              modal: true,
            },
          });
        } else {
          global.showLoginForm && global.showLoginForm();
          history.goBackOrReplace(location.pathname);
        }
      }
    },
    [isUserConnected, dispatch, missionId, history, location],
  );

  const otherProps = useMemo(() => ({ onClick }), [onClick]);

  const enhancedProps = useMergeProps({ ownProps, selectedProps, otherProps });

  return (
    <>
      <MissionEnrollmentButton {...enhancedProps} />
      {createPortal(
        <StyledSimpleInfosModal
          isOpened={optInErrorModalIsOpened}
          onClose={() => {
            setOptInErrorModalIsOpened(false);

            setTimeout(() => {
              setOptInError(null);
            }, 300);
          }}
        >
          {errorCodeLabel}
        </StyledSimpleInfosModal>,
        getPortalRootDOMElement(),
      )}
    </>
  );
};

export default MissionEnrollmentButtonContainer;
